import { HTMLInputTypeAttribute, useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';
import { AutoComplete, AutoCompleteChangeEvent } from 'primereact/autocomplete';
import { classNames } from 'primereact/utils';
import { State } from '../../models/state';

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

interface GoogleAddressResult {
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  place_id: string;
}

export interface AddressComponents {
  address: string;
  city: string;
  state: State | null;
  zipCode: string;
}

export type FormGoogleAddressLookupProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  className?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  type?: HTMLInputTypeAttribute | undefined;
  onChange?: (e: AutoCompleteChangeEvent) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onAddressSelected?: (components: AddressComponents) => void;
};

const FormGoogleAddressLookup = <TFieldValues extends FieldValues>({
  ...props
}: FormGoogleAddressLookupProps<TFieldValues>): JSX.Element => {
  const { className, control, field, errors, label, disabled, onChange, onAddressSelected } = props;

  const [suggestions, setSuggestions] = useState<GoogleAddressResult[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const dummyElement = useRef<HTMLDivElement>(null);
  const fieldValueRef = useRef<any>(null);

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  useEffect(() => {
    const initializeGoogleMaps = () => {
      try {
        if (!window.google) {
          setApiError('Google Maps API failed to load. Please check your API key configuration.');
          return;
        }

        if (!window.google.maps) {
          setApiError('Google Maps API failed to initialize.');
          return;
        }

        autocompleteService.current = new google.maps.places.AutocompleteService();
        if (dummyElement.current) {
          placesService.current = new google.maps.places.PlacesService(dummyElement.current);
        }
      } catch (error) {
        setApiError('Failed to initialize Google Maps services. Please try again later.');
        console.error('Google Maps initialization error:', error);
      }
    };

    initializeGoogleMaps();

    // Add error event listener for Google Maps
    const handleGoogleMapsError = () => {
      setApiError('Google Maps API encountered an error. Please check your configuration.');
    };

    window.gm_authFailure = handleGoogleMapsError;

    return () => {
      window.gm_authFailure = undefined;
    };
  }, []);

  const search = (event: { query: string }) => {
    if (!autocompleteService.current) {
      setApiError('Address lookup service is not available.');
      return;
    }

    const request: google.maps.places.AutocompletionRequest = {
      input: event.query,
      componentRestrictions: { country: 'us' },
      types: ['address'],
    };

    autocompleteService.current.getPlacePredictions(
      request,
      (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions as GoogleAddressResult[]);
        } else {
          setApiError('Failed to fetch address suggestions.');
          setSuggestions([]);
        }
      }
    );
  };

  const onSelect = (event: { value: GoogleAddressResult }) => {
    if (!placesService.current || !event.value.place_id) return;

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId: event.value.place_id,
      fields: ['address_component'],
    };

    placesService.current.getDetails(request, (place: google.maps.places.PlaceResult | null) => {
      if (place && place.address_components) {
        const addressComponents: AddressComponents = {
          address: '',
          city: '',
          state: null,
          zipCode: '',
        };

        // Street number and street name
        const streetNumber =
          place.address_components.find((component) => component.types.includes('street_number'))?.long_name || '';

        const streetName = place.address_components.find((component) => component.types.includes('route'))?.long_name || '';

        addressComponents.address = `${streetNumber} ${streetName}`.trim();

        // City
        addressComponents.city =
          place.address_components.find((component) => component.types.includes('locality'))?.long_name || '';

        // State
        const state: State | null =
          (place.address_components.find((component) => component.types.includes('administrative_area_level_1'))
            ?.short_name as State) || null;

        if (state) {
          addressComponents.state = state;
        }

        // Zip Code
        addressComponents.zipCode =
          place.address_components.find((component) => component.types.includes('postal_code'))?.long_name || '';

        if (onAddressSelected) {
          onAddressSelected(addressComponents);
        }

        // Update the displayed value to show only the street address portion
        if (fieldValueRef.current) {
          fieldValueRef.current(addressComponents.address);
        }
      }
    });
  };

  const itemTemplate = (item: GoogleAddressResult) => {
    return (
      <div className="flex flex-column">
        <span className="font-bold">{item.structured_formatting.main_text}</span>
        <span className="text-sm">{item.structured_formatting.secondary_text}</span>
      </div>
    );
  };

  return (
    <div className={classNames('field', className)}>
      <div ref={dummyElement} style={{ display: 'none' }} />
      <Controller
        name={field}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <label htmlFor={field.name} className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}>
              {label}
            </label>
            <AutoComplete
              value={field.value}
              suggestions={suggestions}
              completeMethod={search}
              field="description"
              onChange={
                onChange ?? ((e) => (e.target.value ? field.onChange(e.target.value as any) : field.onChange(null as any)))
              }
              onSelect={(e) => {
                // Save reference to field.onChange for use in onSelect callback
                fieldValueRef.current = field.onChange;
                onSelect(e);
              }}
              itemTemplate={itemTemplate}
              className={classNames({ 'p-invalid': fieldState.error, disabled })}
              placeholder={label}
              style={{ width: '100%' }}
            />
            {apiError && <small className="p-error block">{apiError}</small>}
          </>
        )}
      />
      {errorTemplate()}
    </div>
  );
};

export default FormGoogleAddressLookup;
